import { useState } from 'react'

import { BuyBox } from 'src/components/BuyBox/BuyBox'
import { PurchaseTypeSwitch } from 'src/components/BuyBox/PurchaseTypeSwitch'
import { VariantOptions } from 'src/components/BuyBox/VariantOptions'
import { ProductAccordion } from 'src/components/ProductAccordion/ProductAccordion'
import { ProductBreadCrumbs } from 'src/components/ProductDetail/ProductBreadCrumbs'
import { ProductHeadline } from 'src/components/ProductDetail/ProductHeadline'
import { ProductMatchingProducts } from 'src/components/ProductDetail/ProductMatchingProducts'
import { config } from 'src/config'
import { PurchaseType } from 'src/constants'
import { subscriptionSellingPlanGenerator } from 'src/functions/product/subscriptionSellingPlanGenerator'
import { getMetafield } from 'src/functions/util/getMetafield'

import styles from './ProductInfo.module.scss'

import type { Product, Collection, ProductVariant } from 'src/data/graphql-generated'
import type { ProductRating } from 'src/types/Product'

interface ProductInfoProps {
  product: Product
  variant: ProductVariant
  rating: NonNullable<ProductRating>
  breadcrumbs: (Collection | undefined)[]
  setVariantSku: (sku: ProductVariant['sku']) => void
  isRenderedWithinModal?: boolean
}

export const ProductInfo = ({
  product,
  variant,
  rating,
  breadcrumbs,
  setVariantSku,
  isRenderedWithinModal,
}: ProductInfoProps) => {
  const { descriptionHtml, productType } = product

  const [purchaseType, setPurchaseType] = useState<PurchaseType>(PurchaseType.oneTime)
  const [sellingPlanInterval, setSellingPlanInterval] = useState(config.defaultSellingPlanInterval)

  const variantDescription = getMetafield(variant.metafields, 'information', 'description')

  const {
    sellingPlanGroups: { nodes: sellingPlansGroups },
  } = product

  const productDescription = variantDescription || descriptionHtml

  const generatedSellingPlans = subscriptionSellingPlanGenerator(sellingPlansGroups)
  const { hasSellingPlans } = generatedSellingPlans

  return (
    <div className={styles.container}>
      <ProductBreadCrumbs breadcrumbs={breadcrumbs} hidden="mobile" />

      <VariantOptions product={product} variant={variant} setVariantSku={setVariantSku} />

      <ProductHeadline product={product} rating={rating} descriptionHtml={productDescription} />

      {hasSellingPlans && (
        <PurchaseTypeSwitch
          purchaseType={purchaseType}
          setPurchaseType={setPurchaseType}
          productType={productType}
          sellingPlanInterval={sellingPlanInterval}
          setSellingPlanInterval={setSellingPlanInterval}
          coffeeSubscriptionSellingPlans={generatedSellingPlans}
        />
      )}

      <BuyBox
        product={product}
        variant={variant}
        purchaseType={purchaseType}
        generatedSellingPlans={generatedSellingPlans}
        sellingPlanInterval={sellingPlanInterval}
        isRenderedWithinModal={isRenderedWithinModal}
      />

      <ProductMatchingProducts variant={variant} />

      <ProductAccordion product={product} variant={variant} />
    </div>
  )
}
